/**
 * ExecutionNotificationsProvider - Global polling + transition notifications
 *
 * Mounted once at the app root (``RootLayout``) so it keeps polling regardless
 * of which tab/route the user is currently viewing. This fixes the
 * long-standing UX gap where extraction failure/success toasts only fired when
 * the user happened to be on the exact tab that had the inline
 * ``ExecutionProgressMonitor`` mounted.
 *
 * That root mount also covers the public routes, so the polling is gated on
 * the auth store -- otherwise every interval 401s twice on the login page
 * (#1027).
 *
 * Approach:
 *   1. Poll ``GET /connectors/executions?status=running`` AND ``status=pending``
 *      every ~3s while the user is authenticated.
 *   2. Track the last known status of each execution in a Map.
 *   3. When an execution disappears from the active set (or transitions to a
 *      terminal status), fetch its final state and fire a single toast via
 *      the existing shadcn toast system.
 *
 * Does NOT replace ``useExecutionPolling`` / ``ExecutionToast`` /
 * ``ExecutionProgressMonitor`` -- those remain the right tool for inline,
 * per-execution displays inside the connector detail drawer.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/store/auth';
import { useToast } from '@/hooks/useToast';
import { ToastAction } from '@/components/ui/toast';
import type { ConnectorExecution, ExecutionStatus } from '@/types/connectors';

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES: ReadonlyArray<ExecutionStatus> = [
  'success',
  'partial',
  'failed',
  'cancelled',
  'timeout',
  'skipped',
];

interface ExecutionListResponse {
  items: ConnectorExecution[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

async function fetchActiveExecutions(): Promise<ConnectorExecution[]> {
  // Pull both pending and running executions across every connector type.
  const [running, pending] = await Promise.all([
    apiClient
      .get<ExecutionListResponse>('/connectors/executions', {
        params: { status: 'running', page_size: 100 },
      })
      .then((r) => r.data?.items ?? [])
      .catch(() => [] as ConnectorExecution[]),
    apiClient
      .get<ExecutionListResponse>('/connectors/executions', {
        params: { status: 'pending', page_size: 100 },
      })
      .then((r) => r.data?.items ?? [])
      .catch(() => [] as ConnectorExecution[]),
  ]);
  // De-dupe by execution_id (rare, but safe).
  const map = new Map<string, ConnectorExecution>();
  for (const e of [...running, ...pending]) {
    map.set(e.execution_id, e);
  }
  return Array.from(map.values());
}

async function fetchExecutionFinalState(
  connectorType: string,
  executionId: string
): Promise<ConnectorExecution | null> {
  try {
    const resp = await apiClient.get<
      ConnectorExecution | { execution: ConnectorExecution }
    >(`/connectors/${connectorType}/executions/${executionId}`);
    // The per-execution endpoint wraps its payload as ``{ execution: {...} }``
    // while the list endpoint returns flat objects. Tolerate both shapes so
    // the terminal-status check below doesn't silently fail.
    const data = resp.data as
      | ConnectorExecution
      | { execution: ConnectorExecution };
    if (data && typeof data === 'object' && 'execution' in data) {
      return (data as { execution: ConnectorExecution }).execution ?? null;
    }
    return data as ConnectorExecution;
  } catch {
    return null;
  }
}

function variantForStatus(
  status: ExecutionStatus
): 'default' | 'destructive' {
  return status === 'failed' || status === 'timeout' ? 'destructive' : 'default';
}

function titleForStatus(status: ExecutionStatus, connectorName: string): string {
  switch (status) {
    case 'success':
      return `${connectorName}: extraction succeeded`;
    case 'partial':
      return `${connectorName}: extraction partially succeeded`;
    case 'failed':
      return `${connectorName}: extraction failed`;
    case 'cancelled':
      return `${connectorName}: extraction cancelled`;
    case 'timeout':
      return `${connectorName}: extraction timed out`;
    case 'skipped':
      return `${connectorName}: extraction skipped`;
    default:
      return `${connectorName}: extraction ${status}`;
  }
}

function describeExecution(exec: ConnectorExecution): string {
  const parts: string[] = [];
  if (exec.expected_resources != null) {
    const done = (exec.queries_successful ?? 0) + (exec.queries_failed ?? 0);
    parts.push(`${done} of ${exec.expected_resources} resources`);
  } else if ((exec.queries_successful ?? 0) + (exec.queries_failed ?? 0) > 0) {
    parts.push(
      `${exec.queries_successful ?? 0} succeeded, ${exec.queries_failed ?? 0} failed`
    );
  }
  if (exec.records_processed) {
    parts.push(`${exec.records_processed.toLocaleString()} records`);
  }
  if (exec.error_message) {
    parts.push(exec.error_message.slice(0, 140));
  }
  return parts.join(' • ');
}

export interface ExecutionNotificationsProviderProps {
  children?: React.ReactNode;
  /** Override polling interval (ms). Mainly for tests. */
  pollIntervalMs?: number;
  /** Override the active-fetch function. Mainly for tests. */
  fetchActive?: () => Promise<ConnectorExecution[]>;
  /** Override the per-execution detail fetch. Mainly for tests. */
  fetchDetails?: (
    connectorType: string,
    executionId: string
  ) => Promise<ConnectorExecution | null>;
  /** Disable navigate() side effect (tests). */
  disableNavigate?: boolean;
}

export function ExecutionNotificationsProvider({
  children,
  pollIntervalMs = POLL_INTERVAL_MS,
  fetchActive = fetchActiveExecutions,
  fetchDetails = fetchExecutionFinalState,
  disableNavigate = false,
}: ExecutionNotificationsProviderProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // RootLayout always renders inside a Router, so this is always safe to call
  // unconditionally (tests wrap in MemoryRouter + mock useNavigate).
  const navigate = useNavigate();

  // Map of execution_id -> last seen status. We treat anything in this map
  // as "previously observed as active". When it disappears or flips to a
  // terminal state, we fire a toast exactly once.
  const knownRef = React.useRef<Map<string, ConnectorExecution>>(new Map());
  // Set of execution_ids we've already notified about (to avoid duplicates
  // across remounts/refetches of the same terminal execution).
  const notifiedRef = React.useRef<Set<string>>(new Set());

  // The provider is mounted in RootLayout, which wraps the public routes
  // (/login, /set-password, ...) as well as the protected ones. Polling
  // without a session just 401s twice per interval (#1027), so gate on the
  // auth store. ``isInitializing`` covers app mount, where the silent refresh
  // against the httpOnly cookie hasn't resolved yet.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitializing = useAuthStore((s) => s.isInitializing);

  // Bumped on every sign-out so the next session starts from a clean slate:
  // it re-keys the query (a poll still in flight at sign-out resolves into the
  // retired key and is never observed) alongside clearing the tracking refs
  // below. Without that, an execution seen as active before sign-out is absent
  // from the next session's first poll, which the transition effect reads as
  // "just went terminal" -- toasting the previous user's run to the new one.
  //
  // The epoch bump itself is computed during render rather than in a
  // useEffect -- see
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes.
  // `prevIsAuthenticated` mirrors the last-rendered auth state; when it
  // disagrees with the current one we're seeing the sign-out transition for
  // the first time, so the retired key is in place for this very render --
  // before the stale-keyed query's in-flight response could otherwise be
  // read under the reused key. (Refs can't be touched during render, so the
  // tracking-map reset lives in the plain effect below instead.)
  const [prevIsAuthenticated, setPrevIsAuthenticated] =
    React.useState(isAuthenticated);
  const [authEpoch, setAuthEpoch] = React.useState(0);
  if (isAuthenticated !== prevIsAuthenticated) {
    setPrevIsAuthenticated(isAuthenticated);
    if (!isAuthenticated) {
      setAuthEpoch((n) => n + 1);
    }
  }

  const query = useQuery({
    queryKey: ['executionNotifications', 'active', authEpoch],
    queryFn: fetchActive,
    enabled: isAuthenticated && !isInitializing,
    refetchInterval: pollIntervalMs,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: 1,
  });

  React.useEffect(() => {
    if (isAuthenticated) return;
    knownRef.current.clear();
    notifiedRef.current.clear();
  }, [isAuthenticated]);

  React.useEffect(() => {
    // A poll that was in flight at sign-out still resolves; ignore its payload
    // rather than let it re-populate the tracking map after the reset above.
    if (!isAuthenticated) return;
    const items = query.data;
    if (!items) return;

    const seen = new Set<string>();
    for (const exec of items) {
      seen.add(exec.execution_id);
      knownRef.current.set(exec.execution_id, exec);
    }

    // Anything we previously knew about but no longer see in active list ->
    // it transitioned to a terminal state. Fetch the final state for the
    // toast detail.
    const transitioned: ConnectorExecution[] = [];
    for (const [id, prev] of knownRef.current.entries()) {
      if (!seen.has(id) && !notifiedRef.current.has(id)) {
        transitioned.push(prev);
      }
    }

    if (transitioned.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const prev of transitioned) {
        notifiedRef.current.add(prev.execution_id);
        const finalExec =
          (await fetchDetails(prev.connector_type, prev.execution_id)) || prev;
        if (cancelled) return;

        const status = finalExec.status;
        if (!TERMINAL_STATUSES.includes(status)) {
          // Wasn't actually terminal (e.g. transient list miss); allow a future
          // notification by removing from notified set, leave knownRef as-is.
          notifiedRef.current.delete(prev.execution_id);
          continue;
        }

        const connectorName = finalExec.connector_type || prev.connector_type;
        toast({
          variant: variantForStatus(status),
          title: titleForStatus(status, connectorName),
          description: describeExecution(finalExec) || undefined,
          action: !disableNavigate
            ? (
                <ToastAction
                  altText="View details"
                  onClick={() => {
                    navigate(
                      `/connectors?connector=${encodeURIComponent(
                        connectorName
                      )}&execution=${encodeURIComponent(prev.execution_id)}&tab=history`
                    );
                  }}
                >
                  View details
                </ToastAction>
              )
            : undefined,
        });

        // Drop from active map once notified.
        knownRef.current.delete(prev.execution_id);

        // Invalidate any per-execution polling caches so the inline UI
        // refreshes immediately if the user navigates over.
        queryClient.invalidateQueries({
          queryKey: ['executionPolling', connectorName, prev.execution_id],
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    query.data,
    isAuthenticated,
    toast,
    queryClient,
    fetchDetails,
    navigate,
    disableNavigate,
  ]);

  return <>{children}</>;
}

export default ExecutionNotificationsProvider;
